import { PAGINATION } from '@/constants/pagination.constant';
import { MessageType } from '@/types/common';
import { createAppSlice } from '../createAppSlice';
import { transformerArray } from '../transformer';
import { AGE_TYPE, HISTORY_TYPE } from '@/constants/type';
import { Message } from '@/enums/message.enum';
import { MESSAGE } from '@/constants/message.constant';
import { FOOD_TYPE } from '@/enums/food.enum';

type ComboFoodSliceState = {
  loading: boolean;
  mealLoading: boolean;
  listHealthNutritionProfilesComboFood: ComboFood[];
  listAgeComboFood: AgeComboFood[];
  listGenderComboFood: GenderComboFood[];
  listPhysicalActivityComboFood: PhysicalActivityComboFood[];
  listNutritionHealthStatusComboFood: NutritionHealthStatusComboFood[];
  listNutritionStatusComboFood: NutritionStatusComboFood[];
  listNutritionStatusZScoreComboFood: NutritionStatusComboFood[];
  listNutritionStatus60to19ComboFood: NutritionStatusComboFood[];
  healthNutritionProfiles: HealthNutritionProfiles | null;
  listComboFoodDetail: DetailMealNutritionProfile[];
  foodGroupP: FoodGroup[];
  foodGroupC: FoodGroup[];
  foodGroupV: FoodGroup[];
  foodGroupF: FoodGroup[];
  foodGroupM: FoodGroup[];
  foodGroupO: FoodGroup[];
  message?: MessageType;
  pagination: Pagination;
  paginationMeal: Pagination;
  listHistory: HistoryComboFood[];
  paginationHistory: Pagination;
  downloadTemplateComboFood?: TemplateComboFood;
};

const initialState: ComboFoodSliceState = {
  loading: false,
  mealLoading: false,
  listHealthNutritionProfilesComboFood: [],
  listAgeComboFood: [],
  listGenderComboFood: [],
  listPhysicalActivityComboFood: [],
  listNutritionHealthStatusComboFood: [],
  listNutritionStatusComboFood: [],
  listNutritionStatusZScoreComboFood: [],
  listNutritionStatus60to19ComboFood: [],
  healthNutritionProfiles: null,
  listComboFoodDetail: [],
  foodGroupP: [],
  foodGroupC: [],
  foodGroupV: [],
  foodGroupF: [],
  foodGroupM: [],
  foodGroupO: [],
  pagination: PAGINATION,
  paginationMeal: PAGINATION,
  listHistory: [],
  paginationHistory: PAGINATION,
  downloadTemplateComboFood: undefined
};

export const comboFoodSlice = createAppSlice({
  name: 'comboFood',
  initialState,
  reducers: (create) => ({
    getListHealthNutritionProfilesComboFood: create.asyncThunk(
      async (payload?: {
        customFilter?: FilterComboFood;
        limit?: number;
        page?: number;
      }) => {
        const { customFilter = {}, limit = 10, page = 1 } = payload || {};
        const response = await apiFetcher.post(
          `food-combo/health-nutrition-profiles/get-list-filter?page=${page}&limit=${limit}`,
          customFilter
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHealthNutritionProfilesComboFood = action.payload.listData;
          state.pagination = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHealthNutritionProfilesComboFood = [];
          state.pagination = PAGINATION;
        }
      }
    ),
    createHealthNutritionProfilesComboFood: create.asyncThunk(
      async (payload?: CreateHealthNutritionProfiles) => {
        const response = await apiFetcher.post(
          'food-combo/health-nutrition-profiles',
          payload
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.healthNutritionProfiles = action.payload;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_COMBO_FOOD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.healthNutritionProfiles = null;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_COMBO_FOOD_FAIL
          };
        }
      }
    ),
    deleteHealthNutritionProfilesComboFood: create.asyncThunk(
      async (id?: number) => {
        const response = await apiFetcher.delete(
          `food-combo/health-nutrition-profiles/${id}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHealthNutritionProfilesComboFood =
            state.listHealthNutritionProfilesComboFood.filter(
              (comboFood) => comboFood.PROFILE_ID !== action.payload
            );
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_COMBO_FOOD_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_COMBO_FOOD_FAIL
          };
        }
      }
    ),
    createDetailMeal: create.asyncThunk(
      async (payload?: DetailMealNutritionProfile) => {
        const response = await apiFetcher.post('food-combo/detail', payload);
        return response.data;
      },
      {
        pending: (state) => {
          state.mealLoading = true;
        },
        fulfilled: (state) => {
          state.mealLoading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.CREATE_MEAL_SUCCESS
          };
        },
        rejected: (state) => {
          state.mealLoading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.CREATE_MEAL_FAIL
          };
        }
      }
    ),
    editDetailMeal: create.asyncThunk(
      async (payload?: DetailMealNutritionProfile) => {
        const response = await apiFetcher.put('food-combo/detail', payload);
        return response.data;
      },
      {
        pending: (state) => {
          state.mealLoading = true;
        },
        fulfilled: (state) => {
          state.mealLoading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_MEAL_SUCCESS
          };
        },
        rejected: (state) => {
          state.mealLoading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.UPDATE_MEAL_FAIL
          };
        }
      }
    ),
    deleteDetailMeal: create.asyncThunk(
      async (id: number) => {
        const response = await apiFetcher.delete(`food-combo/detail/${id}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.mealLoading = true;
        },
        fulfilled: (state) => {
          state.mealLoading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.DELETE_MEAL_SUCCESS
          };
        },
        rejected: (state) => {
          state.mealLoading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.DELETE_MEAL_FAIL
          };
        }
      }
    ),
    getGroupFoodMealP: create.asyncThunk(
      async (query?: string) => {
        const dataQuery = query ?? '';
        const response = await apiFetcher.get(
          `foods-group/list?group_name=${FOOD_TYPE.NHOM_CHAT_DAM}&&search=${dataQuery}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.foodGroupP = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.foodGroupP = [];
        }
      }
    ),
    getGroupFoodMealC: create.asyncThunk(
      async (query?: string) => {
        const dataQuery = query ?? '';
        const response = await apiFetcher.get(
          `foods-group/list?group_name=${FOOD_TYPE.NHOM_CHAT_BOT_DUONG}&&search=${dataQuery}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.foodGroupC = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.foodGroupC = [];
        }
      }
    ),
    getGroupFoodMealV: create.asyncThunk(
      async (query?: string) => {
        const dataQuery = query ?? '';
        const response = await apiFetcher.get(
          `foods-group/list?group_name=${FOOD_TYPE.RAU_CU_QUA}&&search=${dataQuery}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.foodGroupV = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.foodGroupV = [];
        }
      }
    ),
    getGroupFoodMealF: create.asyncThunk(
      async (query?: string) => {
        const dataQuery = query ?? '';
        const response = await apiFetcher.get(
          `foods-group/list?group_name=${FOOD_TYPE.TRAI_CAY}&&search=${dataQuery}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.foodGroupF = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.foodGroupF = [];
        }
      }
    ),
    getGroupFoodMealM: create.asyncThunk(
      async (query?: string) => {
        const dataQuery = query ?? '';
        const response = await apiFetcher.get(
          `foods-group/list?group_name=${FOOD_TYPE.SUA}&&search=${dataQuery}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.foodGroupM = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.foodGroupM = [];
        }
      }
    ),
    getGroupFoodMealO: create.asyncThunk(
      async (query?: string) => {
        const dataQuery = query ?? '';
        const response = await apiFetcher.get(
          `foods-group/list?group_name=${FOOD_TYPE.NHOM_GIAU_CHAT_BEO}&&search=${dataQuery}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.foodGroupO = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.foodGroupO = [];
        }
      }
    ),
    getComboFoodDetail: create.asyncThunk(
      async (payload?: {
        profileID: number;
        limit?: number;
        page?: number;
      }) => {
        const { profileID, limit = 10, page = 1 } = payload || {};
        const response = await apiFetcher.get(
          `food-combo/detail?page=${page}&limit=${limit}&profileId=${profileID}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.mealLoading = true;
        },
        fulfilled: (state, action) => {
          state.mealLoading = false;
          state.listComboFoodDetail = action.payload.listData;
          state.paginationMeal = action.payload.paging;
        },
        rejected: (state) => {
          state.mealLoading = false;
          state.listComboFoodDetail = [];
          state.paginationMeal = PAGINATION;
        }
      }
    ),
    getProfileComboFoodDetail: create.asyncThunk(
      async (profileID: number) => {
        const response = await apiFetcher.get(
          `food-combo/health-nutrition-profiles/detail/${profileID}`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.mealLoading = true;
        },
        fulfilled: (state, action) => {
          state.mealLoading = false;
          state.healthNutritionProfiles = action.payload;
        },
        rejected: (state) => {
          state.mealLoading = false;
          state.healthNutritionProfiles = null;
        }
      }
    ),
    getListAgeComboFood: create.asyncThunk(
      async (type?: string) => {
        if (!type) type = 'FOOD_COMBO';
        const response = await apiFetcher.get(`age-ranges?type=${type}`);
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listAgeComboFood = transformerArray(action.payload.listData);
        },
        rejected: (state) => {
          state.loading = false;
          state.listAgeComboFood = [];
        }
      }
    ),
    getListGenderComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('gender');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listGenderComboFood = transformerArray(action.payload.listData);
        },
        rejected: (state) => {
          state.loading = false;
          state.listGenderComboFood = [];
        }
      }
    ),
    getListPhysicalActivityComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('option/get-list-by-type');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listPhysicalActivityComboFood = transformerArray(
            action.payload
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listPhysicalActivityComboFood = [];
        }
      }
    ),
    getListNutritionHealthStatusComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('nutrition-health-statuses');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listNutritionHealthStatusComboFood = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listNutritionHealthStatusComboFood = [];
        }
      }
    ),
    getListNutritionStatusComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          `health-status-bmi?type=${AGE_TYPE.BMI_GE_19_AGE}&option=FOOD_COMBO`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listNutritionStatusComboFood = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listNutritionStatusComboFood = [];
        }
      }
    ),
    getListNutritionStatusZScoreComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          `health-status-bmi?type=${AGE_TYPE.ZSCORE_60}&option=FOOD_COMBO`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listNutritionStatusZScoreComboFood = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listNutritionStatusZScoreComboFood = [];
        }
      }
    ),
    getListNutritionStatus60to19ComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get(
          `health-status-bmi?type=${AGE_TYPE.BMI_60_MONTH_19_AGE}&option=FOOD_COMBO`
        );
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listNutritionStatus60to19ComboFood = transformerArray(
            action.payload.listData
          );
        },
        rejected: (state) => {
          state.loading = false;
          state.listNutritionStatus60to19ComboFood = [];
        }
      }
    ),
    importFile: create.asyncThunk(
      async (formData: FormData) => {
        try {
          const response = await apiFetcher.post(
            'food-combo/import',
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          return response.data;
        } catch (error: any) {
          throw new Error(
            error.response.data.message || MESSAGE.IMPORT_COMBO_FOOD_FAIL
          );
        }
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.IMPORT_COMBO_FOOD_SUCCESS
          };
        },
        rejected: (state, action) => {
          state.loading = false;
          const errorMsg = action.error?.message?.toLowerCase() || '';
          const originalErrorMsg = action.error?.message || '';
          if (
            errorMsg.includes('excel') ||
            errorMsg.includes('images') ||
            errorMsg.includes('template')
          ) {
            state.message = {
              type: Message.ERROR,
              message: MESSAGE.IMPORT_NUTRITION_TEMPLATE_ERROR
            };
          } else if (action.error?.message?.includes('Dữ liệu không hợp lệ!')) {
            state.message = {
              type: Message.ERROR,
              message: MESSAGE.IMPORT_NUTRITION_MISMATCH_ERROR
            };
          } else {
            state.message = {
              type: Message.ERROR,
              message: originalErrorMsg || MESSAGE.IMPORT_COMBO_FOOD_FAIL
            };
          }
        }
      }
    ),
    getHistory: create.asyncThunk(
      async ({ offset, limit }: { offset?: number; limit?: number }) => {
        const params = new URLSearchParams();
        if (offset !== undefined) params.append('offset', offset.toString());
        if (limit !== undefined) params.append('limit', limit.toString());

        const url = `history-import/filter${params.toString() ? `?${params.toString()}` : ''}`;
        const query = {
          type: HISTORY_TYPE.FOOD_COMBO
        };
        const response = await apiFetcher.post(url, query || {});
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.listHistory = transformerArray(action.payload.listData);
          state.paginationHistory = action.payload.paging;
        },
        rejected: (state) => {
          state.loading = false;
          state.listHistory = [];
          state.paginationHistory = PAGINATION;
        }
      }
    ),
    downloadTemplateComboFood: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('template/info?type=FOODCOMBO');
        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.downloadTemplateComboFood = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.downloadTemplateComboFood = undefined;
        }
      }
    ),
    resetHealthNutritionProfiles: create.reducer((state) => {
      state.healthNutritionProfiles = null;
      state.loading = false;
    }),
    resetMessages: create.reducer((state) => {
      state.message = undefined;
    })
  }),
  selectors: {
    watchListHealthNutritionProfilesComboFood: (comboFood) =>
      comboFood.listHealthNutritionProfilesComboFood,
    watchListAgeComboFood: (comboFood) => comboFood.listAgeComboFood,
    watchHealthNutritionProfiles: (comboFood) =>
      comboFood.healthNutritionProfiles,
    watchListGenderComboFood: (comboFood) => comboFood.listGenderComboFood,
    watchListPhysicalActivityComboFood: (comboFood) =>
      comboFood.listPhysicalActivityComboFood,
    watchListNutritionHealthStatusComboFood: (comboFood) =>
      comboFood.listNutritionHealthStatusComboFood,
    watchListNutritionStatusComboFood: (comboFood) =>
      comboFood.listNutritionStatusComboFood,
    watchListNutritionStatusZScoreComboFood: (comboFood) =>
      comboFood.listNutritionStatusZScoreComboFood,
    watchListNutritionStatus60to19ComboFood: (comboFood) =>
      comboFood.listNutritionStatus60to19ComboFood,
    watchFoodGroupP: (comboFood) => comboFood.foodGroupP,
    watchFoodGroupC: (comboFood) => comboFood.foodGroupC,
    watchFoodGroupV: (comboFood) => comboFood.foodGroupV,
    watchFoodGroupF: (comboFood) => comboFood.foodGroupF,
    watchFoodGroupM: (comboFood) => comboFood.foodGroupM,
    watchFoodGroupO: (comboFood) => comboFood.foodGroupO,
    watchListComboFoodDetail: (comboFood) => comboFood.listComboFoodDetail,
    watchComboFoodMessage: (comboFood) => comboFood.message,
    watchComboFoodPagination: (comboFood) => comboFood.pagination,
    watchMealPagination: (comboFood) => comboFood.paginationMeal,
    watchComboFoodLoading: (comboFood) => comboFood.loading,
    watchMealLoading: (comboFood) => comboFood.mealLoading,
    watchListHistory: (comboFood) => comboFood.listHistory,
    watchPaginationHistory: (comboFood) => comboFood.paginationHistory,
    watchDownloadTemplateComboFood: (food) => food.downloadTemplateComboFood
  }
});

export const {
  watchListHealthNutritionProfilesComboFood,
  watchListAgeComboFood,
  watchHealthNutritionProfiles,
  watchListGenderComboFood,
  watchListPhysicalActivityComboFood,
  watchListNutritionHealthStatusComboFood,
  watchListNutritionStatusComboFood,
  watchListNutritionStatusZScoreComboFood,
  watchListNutritionStatus60to19ComboFood,
  watchListComboFoodDetail,
  watchFoodGroupP,
  watchFoodGroupC,
  watchFoodGroupV,
  watchFoodGroupF,
  watchFoodGroupM,
  watchFoodGroupO,
  watchComboFoodMessage,
  watchComboFoodPagination,
  watchMealPagination,
  watchComboFoodLoading,
  watchMealLoading,
  watchListHistory,
  watchPaginationHistory,
  watchDownloadTemplateComboFood
} = comboFoodSlice.selectors;

export const {
  createHealthNutritionProfilesComboFood,
  getListHealthNutritionProfilesComboFood,
  getListAgeComboFood,
  getListGenderComboFood,
  getListPhysicalActivityComboFood,
  getListNutritionHealthStatusComboFood,
  getListNutritionStatusComboFood,
  getListNutritionStatusZScoreComboFood,
  getListNutritionStatus60to19ComboFood,
  getComboFoodDetail,
  createDetailMeal,
  editDetailMeal,
  deleteDetailMeal,
  getGroupFoodMealP,
  getGroupFoodMealC,
  getGroupFoodMealV,
  getGroupFoodMealF,
  getGroupFoodMealM,
  getGroupFoodMealO,
  deleteHealthNutritionProfilesComboFood,
  importFile,
  getHistory,
  getProfileComboFoodDetail,
  resetHealthNutritionProfiles,
  resetMessages,
  downloadTemplateComboFood
} = comboFoodSlice.actions;
